import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

function maskEmail(email: string): string {
  const [local, domain] = email.split("@");
  if (!domain) return email;
  const masked = local.charAt(0) + "*".repeat(Math.max(local.length - 1, 4));
  return `${masked}@${domain}`;
}

export default function VerifyOtp() {
  const location = useLocation();
  const navigate = useNavigate();
  const email = (location.state as { email?: string })?.email;

  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  // If no email in state, redirect back
  if (!email) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[400px] space-y-8 text-center">
          <h1 className="text-2xl font-bold text-foreground">Session expired</h1>
          <p className="text-sm text-muted-foreground">Please start the registration again.</p>
          <Link to="/register" className="text-primary hover:underline font-medium text-sm">
            Back to registration
          </Link>
        </div>
      </div>
    );
  }

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== 6) return;

    setLoading(true);
    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: "email",
      });

      if (error) {
        toast({ title: "Verification failed", description: error.message, variant: "destructive" });
        return;
      }

      navigate("/register/password", { state: { email } });
    } catch {
      toast({ title: "Error", description: "Something went wrong. Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResent(false);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { shouldCreateUser: true },
      });
      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      } else {
        setResent(true);
        setTimeout(() => setResent(false), 5000);
      }
    } catch {
      toast({ title: "Error", description: "Failed to resend code.", variant: "destructive" });
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-[400px] space-y-8">
        {/* Logo Mark */}
        <div className="flex justify-center" />

        {/* Heading */}
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-bold text-foreground">Check your inbox</h1>
          <p className="text-sm text-muted-foreground">
            We've sent you a passcode. Please check your inbox at{" "}
            <span className="font-medium text-foreground">{maskEmail(email)}</span>.
          </p>
        </div>

        {/* OTP Form */}
        <form onSubmit={handleVerify} className="space-y-6">
          <div className="flex justify-center">
            <InputOTP maxLength={6} value={otp} onChange={setOtp}>
              <InputOTPGroup className="gap-2">
                <InputOTPSlot index={0} className="w-12 h-14 text-2xl font-bold" />
                <InputOTPSlot index={1} className="w-12 h-14 text-2xl font-bold" />
                <InputOTPSlot index={2} className="w-12 h-14 text-2xl font-bold" />
                <InputOTPSlot index={3} className="w-12 h-14 text-2xl font-bold" />
                <InputOTPSlot index={4} className="w-12 h-14 text-2xl font-bold" />
                <InputOTPSlot index={5} className="w-12 h-14 text-2xl font-bold" />
              </InputOTPGroup>
            </InputOTP>
          </div>

          <Button type="submit" className="w-full" disabled={loading || otp.length !== 6}>
            {loading ? "Verifying…" : "Verify"}
          </Button>
        </form>

        {/* Resend & wrong email */}
        <div className="text-center space-y-2">
          <button
            type="button"
            onClick={handleResend}
            disabled={resending}
            className="text-sm text-primary hover:underline font-medium disabled:opacity-50"
          >
            {resending ? "Resending…" : "Resend code"}
          </button>
          {resent && (
            <p className="text-xs text-muted-foreground">Code resent</p>
          )}
          <p className="text-sm text-muted-foreground">
            <Link to="/register" className="text-primary hover:underline font-medium">
              Wrong email?
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
